import { useEffect, useMemo, useRef, useState } from 'react';

import { extractLocalFrameThumbnails } from '../../services/LocalFrameThumbnails';

type ClipRange = { startTime: number; duration: number };

/** Match the producer/saved-card convention of sampling three seconds in. */
export function getLocalClipThumbnailFrame(clip: ClipRange): number {
  const start = Number.isFinite(clip.startTime) ? clip.startTime : 0;
  const duration = Number.isFinite(clip.duration) ? clip.duration : 0;
  const offset = Math.min(3, Math.max(0, duration / 2));
  // LocalFrameThumbnails samples frame N at N - 0.5 seconds.
  return Math.max(1, Math.round(start + offset + 0.5));
}

function revokeBlobUrls(urls: Iterable<string>) {
  for (const url of urls) {
    if (url.startsWith('blob:')) URL.revokeObjectURL(url);
  }
}

/**
 * Cut one small poster per produced clip through one temporary video element.
 * This deliberately batches the seeks: a multi-gigabyte upload must not be
 * mounted into every result card just to paint its thumbnail.
 */
export function useLocalClipProducerThumbnails(
  objectUrl: string | null,
  clips: ClipRange[],
  enabled: boolean,
): ReadonlyMap<number, string> {
  const [posters, setPosters] = useState<Map<number, string>>(new Map());
  const cacheRef = useRef<Map<number, string>>(new Map());
  const frameNumbers = useMemo(
    () => [...new Set(clips.map(getLocalClipThumbnailFrame))],
    [clips],
  );
  const frameKey = frameNumbers.join(',');

  useEffect(() => {
    const cache = cacheRef.current;
    revokeBlobUrls(cache.values());
    cache.clear();
    setPosters(new Map());

    return () => {
      revokeBlobUrls(cache.values());
      cache.clear();
    };
  }, [objectUrl, enabled]);

  useEffect(() => {
    if (!enabled || !objectUrl || frameNumbers.length === 0) return;
    const needed = frameNumbers.filter(num => !cacheRef.current.has(num));
    if (needed.length === 0) return;

    const controller = new AbortController();
    void extractLocalFrameThumbnails(
      objectUrl,
      needed,
      187,
      controller.signal,
    ).then(frames => {
      if (controller.signal.aborted) {
        revokeBlobUrls(frames.map(frame => frame.url));
        return;
      }
      for (const frame of frames) {
        const previous = cacheRef.current.get(frame.frameNumber);
        if (
          previous &&
          previous !== frame.url &&
          previous.startsWith('blob:')
        ) {
          URL.revokeObjectURL(previous);
        }
        cacheRef.current.set(frame.frameNumber, frame.url);
      }
      setPosters(new Map(cacheRef.current));
    });

    return () => controller.abort();
    // `frameKey` is the stable value dependency; frameNumbers is rebuilt from
    // the clips array each render while streamed producer results arrive.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, objectUrl, frameKey]);

  return posters;
}
